import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import toast from 'react-hot-toast';

import { editeursAPI, livresAPI } from '../services/api';
import LivreCard from '../components/LivreCard/LivreCard';

function EditeurDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Sélectionner la locale date-fns selon la langue i18n
  const dateLocale = i18n.language === 'en' ? enUS : fr;

  const { data: editeur, isLoading: editeurLoading, error: editeurError } = useQuery(
    ['editeur', id],
    () => editeursAPI.getById(id).then(res => res.data.data),
    {
      enabled: !!id,
    }
  );

  // Récupérer les livres de cet éditeur
  const { data: livresData, isLoading: livresLoading } = useQuery(
    ['livres-editeur', id],
    () => livresAPI.getAll({ editeur_id: id, limit: 100 }).then(res => res.data),
    {
      enabled: !!id,
    }
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Mutation pour supprimer l'éditeur
  const deleteEditeurMutation = useMutation(
    () => editeursAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('editeurs');
        toast.success(t('editeurDetailPage.deleteSuccess'));
        navigate('/editeurs');
      },
      onError: (error) => {
        if (error.response?.status === 409 || error.response?.status === 400) {
          toast.error(t('editeurDetailPage.deleteErrorWithBooks'));
        } else {
          toast.error(error.response?.data?.message || t('editeurDetailPage.deleteError'));
        }
      }
    }
  );

  const handleEditClick = () => {
    navigate(`/editeurs/${id}/modifier`);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    deleteEditeurMutation.mutate();
    setDeleteDialogOpen(false);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Fonctions pour LivreCard
  const handleView = (livreId) => {
    navigate(`/livres/${livreId}`);
  };

  const handleEdit = (livreId) => {
    navigate(`/livres/${livreId}/modifier`);
  };

  const handleAuthorClick = (authorId) => {
    if (authorId) {
      navigate(`/auteurs/${authorId}`);
    }
  };

  const handleEditeurClick = (editeurId) => {
    if (editeurId) {
      navigate(`/editeurs/${editeurId}`);
    }
  };

  const handlePhotoClick = (livreId) => {
    navigate(`/livres/${livreId}`);
  };

  if (editeurLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (editeurError) {
    return (
      <Alert severity="error">
        {t('editeurDetailPage.loadingError', { message: editeurError.message })}
      </Alert>
    );
  }

  if (!editeur) {
    return (
      <Alert severity="info">
        {t('editeurDetailPage.notFound')}
      </Alert>
    );
  }

  const livres = livresData?.data || [];

  // Calculer les statistiques des livres
  const stats = {
    total: livres.length,
    lus: livres.filter(l => l.statut_lecture === 'lu').length,
    enCours: livres.filter(l => l.statut_lecture === 'en_cours').length,
    nonLus: livres.filter(l => l.statut_lecture === 'non_lu').length,
  };

  return (
    <Box>
      {/* Header avec navigation */}
      <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton onClick={() => navigate('/editeurs')} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            component="h1" 
            sx={{ 
              flexGrow: 1, 
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: 500,
            }}
          >
            {t('editeurDetailPage.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              sx={{ 
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t('editeurDetailPage.edit')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
              sx={{ 
                height: '52px',
                borderRadius: 2,
                fontSize: '16px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {t('editeurDetailPage.delete')}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Nom de l'éditeur */}
      <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
        {editeur.nom}
      </Typography>

      {/* Informations (adresse et site web) */}
      <Box sx={{ mb: 4 }}>
        {editeur.adresse && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body1" color="text.primary">
              {editeur.adresse}
            </Typography>
          </Box>
        )}
        {editeur.site_web && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LanguageIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="body1" component="span" color="text.primary">
              {t('editeurDetailPage.website')}{' '}
            </Typography>
            <MuiLink 
              href={editeur.site_web} 
              target="_blank" 
              rel="noopener noreferrer"
              sx={{ ml: 0.5 }}
            >
              {editeur.site_web}
            </MuiLink>
          </Box>
        )}
      </Box>

      {/* Statistiques */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        {/* Carte Total Livres */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'primary.main',
              mb: 0.5,
            }}
          >
            {stats.total}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              fontWeight: 500,
            }}
          >
            {stats.total === 1 ? t('editeurDetailPage.book') : t('editeurDetailPage.books')}
          </Typography>
        </Box>

        {/* Carte Lus */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(46, 125, 50, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'success.main',
              mb: 0.5,
            }}
          >
            {stats.lus}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'success.main',
              fontWeight: 500,
            }}
          >
            {t('editeurDetailPage.read')}
          </Typography>
        </Box>

        {/* Carte En cours */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(237, 108, 2, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'warning.main',
              mb: 0.5,
            }}
          >
            {stats.enCours}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'warning.main',
              fontWeight: 500,
            }}
          >
            {t('editeurDetailPage.inProgress')}
          </Typography>
        </Box>

        {/* Carte Non lus */}
        <Box
          sx={{
            flex: '1 1 auto',
            minWidth: '120px',
            p: 2,
            borderRadius: 2,
            backgroundColor: 'rgba(97, 97, 97, 0.08)',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '32px',
              fontWeight: 700,
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            {stats.nonLus}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {t('editeurDetailPage.notRead')}
          </Typography>
        </Box>
      </Box>

      {/* Métadonnées */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: '20px', mr: 1, flexShrink: 0 }} />
          <Typography 
            variant="h6" 
            component="h3"
            sx={{ 
              fontSize: '12px',
              fontWeight: 600,
              color: 'oklch(0.551 0.027 264.364)',
            }}
          >
            {t('editeurDetailPage.metadata')}
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap: { xs: 0, sm: 2 },
          ml: 0 
        }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '14px',
              fontWeight: 400,
              color: 'oklch(0.446 0.03 256.802)',
              mb: { xs: 0.5, sm: 0 },
            }}
          >
            {t('editeurDetailPage.createdAt')} {editeur.created_at ? format(new Date(editeur.created_at), 'dd MMMM yyyy', { locale: dateLocale }) : 'Invalid Date'}
          </Typography>
          {editeur.updated_at && editeur.updated_at !== editeur.created_at && (
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '14px',
                fontWeight: 400,
                color: 'oklch(0.446 0.03 256.802)',
              }}
            >
              {t('editeurDetailPage.updatedAt')} {format(new Date(editeur.updated_at), 'dd MMMM yyyy', { locale: dateLocale })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Liste des livres */}
      <Box>
        <Typography 
          variant="h6" 
          component="h3"
          sx={{ 
            mb: 2,
            fontWeight: 700,
            fontSize: '16px',
          }}
        >
          {t('editeurDetailPage.booksInLibrary')}
        </Typography>
        
        {livresLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : livres.length === 0 ? (
          <Alert severity="info">
            {t('editeurDetailPage.noBooks')}
          </Alert>
        ) : (
          <>
            {livres.length >= 100 && editeur.nombre_livres > 100 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('editeurDetailPage.showingFirst', { total: editeur.nombre_livres })}
              </Alert>
            )}
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { 
                xs: '1fr', 
                sm: 'repeat(2, 1fr)', 
                md: 'repeat(3, 1fr)', 
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(6, 1fr)'
              }, 
              gap: { xs: 2, md: 3 },
              width: '100%'
            }}>
              {livres.map((livre) => (
                <Box key={livre.id} sx={{ width: '100%', minWidth: 0 }}>
                  <LivreCard
                    livre={livre}
                    onView={handleView}
                    onEdit={handleEdit}
                    onAuthorClick={handleAuthorClick}
                    onEditeurClick={handleEditeurClick}
                    onPhotoClick={handlePhotoClick}
                    availableTags={[]}
                    onTagAdd={null}
                    onTagRemove={null}
                    isTagLoading={false}
                    queryClient={queryClient}
                  />
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {t('editeurDetailPage.confirmDeleteTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('editeurDetailPage.deleteConfirmEditeur', { name: editeur.nom })}
          </DialogContentText>
          {stats.total > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {stats.total === 1 
                ? t('editeurDetailPage.deleteWarning', { count: stats.total })
                : t('editeurDetailPage.deleteWarningPlural', { count: stats.total })
              }
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            {t('editeurDetailPage.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleteEditeurMutation.isLoading || stats.total > 0}
          >
            {deleteEditeurMutation.isLoading ? t('editeurDetailPage.deleting') : t('editeurDetailPage.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EditeurDetailPage;

